import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ArrowLeftCircle } from 'src/common/icons';
import { Button, Input } from 'src/components';

import styles from '../Modal.module.scss';

import messages from '../messages';

type TAssetUpdatedConfirmationDialog = {
  // The save callbacks may return a promise. On success the parent closes this
  // dialog; when it resolves and the dialog is still open the save failed (the
  // parent has said why), so the button comes out of its loading state.
  onCreateNewAsset: (assetName: string, includeMetadata: boolean) => void | Promise<unknown>;
  onReplaceOriginal: () => void | Promise<unknown>;
  /** `isPublic` applies to a conversion only — a new asset stays private. */
  onCreateConversion: (conversionName: string, isPublic: boolean) => void | Promise<unknown>;
  onDownloadConversion: () => void;
  onCancel: () => void;
  assetType: 'original' | 'conversion';
  defaultAssetName?: string;
  defaultConversionName?: string;
};

export const AssetUpdatedDialog: React.FC<TAssetUpdatedConfirmationDialog> = ({
  onCreateNewAsset,
  onCreateConversion,
  onReplaceOriginal,
  onDownloadConversion,
  onCancel,
  assetType,
  defaultAssetName = '',
  defaultConversionName = '',
}) => {
  const [showNewAssetOptions, setShowNewAssetOptions] = useState(false);
  const [showConversionOptions, setShowConversionOptions] = useState(false);
  const [showReplaceOptions, setShowReplaceOptions] = useState(false);
  const [saveMetadata, setSaveMetadata] = useState(true);
  // Publish the conversion by default: a format is what the dynamic image
  // sizing API (/conversions) serves, so it is normally produced in order to be
  // used. New assets have no such control — the server always creates their
  // originals private.
  const [isPublic, setIsPublic] = useState(true);
  const [conversionName, setConversionName] = useState(defaultConversionName);
  const [assetName, setAssetName] = useState(defaultAssetName);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Show the spinner while `save` runs; clear it if the dialog is still open
  // afterwards — a refused save (e.g. writes paused, #1215) used to leave the
  // button spinning forever.
  const runSave = (save: () => void | Promise<unknown>) => {
    setLoading(true);
    Promise.resolve(save()).finally(() => {
      if (mounted.current) setLoading(false);
    });
  };

  const handleCreateConversion = () => {
    if (!conversionName) {
      setError('Conversion name is required');
      return;
    }

    if (conversionName.length > 20) {
      setError('Conversion name must be 20 characters or less');
      return;
    }

    if (!/^[a-zA-Z0-9_-]{1,}$/.test(conversionName)) {
      setError('Conversion name must be alphanumeric and can contain "-" and "_"');
      return;
    }

    runSave(() => onCreateConversion(conversionName, isPublic));
  };

  const handleAddAsset = () => {
    if (!assetName) {
      setError('Asset name is required');
      return;
    }

    // The 20-char limit applies to the base name, leaving room for a ".ext".
    const baseName = assetName.replace(/\.[^.]+$/, '');
    if (baseName.length > 20) {
      setError('Asset name must be 20 characters or less');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9]+)?$/.test(assetName)) {
      setError('Asset name must be alphanumeric and can contain "-" and "_"');
      return;
    }

    runSave(() => onCreateNewAsset(assetName, saveMetadata));
  };

  const handleDownloadConversion = () => {
    setLoading(true);
    onDownloadConversion();
  };
  const handleReplaceOriginal = () => {
    if (assetType === 'original') {
      setShowReplaceOptions(true);
    } else {
      runSave(onReplaceOriginal);
    }
  };
  const handleReplaceOriginalImage = () => {
    runSave(onReplaceOriginal);
  };
  return (
    <div className={styles.AssetUpdatedConfirmationDialogContainer}>
      <div className={styles.AssetUpdatedConfirmationDialog}>
        {showConversionOptions || showReplaceOptions || showNewAssetOptions ? (
          <>
            {showConversionOptions && (
              <>
                <Button
                  icon={<ArrowLeftCircle />}
                  mode="flat"
                  size="small"
                  onClick={() => setShowConversionOptions(false)}
                >
                  <FormattedMessage {...messages.goBack} />
                </Button>
                <div className={styles.conversionForm}>
                  <Input
                    type="text"
                    variant="secondary"
                    value={conversionName}
                    onChange={(value) => setConversionName(value)}
                    placeholder="Type a name for the conversion"
                    label="Conversion Name"
                    className={styles.conversionFormInput}
                    error={error}
                  />
                  <div className={styles.accessChoice}>
                    <span className={styles.accessChoiceLabel}>
                      <FormattedMessage {...messages.cutoutAccessLabel} />
                    </span>
                    <div className={styles.accessChoiceOptions}>
                      <button
                        type="button"
                        className={isPublic ? styles.accessChoiceOptionOn : styles.accessChoiceOption}
                        aria-pressed={isPublic}
                        onClick={() => setIsPublic(true)}
                        data-test="pintura-access-public"
                      >
                        <FormattedMessage {...messages.cutoutAccessPublic} />
                      </button>
                      <button
                        type="button"
                        className={!isPublic ? styles.accessChoiceOptionOn : styles.accessChoiceOption}
                        aria-pressed={!isPublic}
                        onClick={() => setIsPublic(false)}
                        data-test="pintura-access-private"
                      >
                        <FormattedMessage {...messages.cutoutAccessPrivate} />
                      </button>
                    </div>
                    <span className={styles.accessChoiceHint}>
                      <FormattedMessage
                        {...(isPublic ? messages.cutoutAccessPublicHint : messages.cutoutAccessPrivateHint)}
                      />
                    </span>
                  </div>

                  <Button type="button" mode="primary" size="small" onClick={handleCreateConversion} loading={loading}>
                    <FormattedMessage {...messages.saveConversion} />
                  </Button>
                </div>
              </>
            )}
            {showNewAssetOptions && (
              <>
                <Button
                  icon={<ArrowLeftCircle />}
                  mode="flat"
                  size="small"
                  onClick={() => setShowNewAssetOptions(false)}
                >
                  <FormattedMessage {...messages.goBack} />
                </Button>
                <div className={styles.conversionForm}>
                  <Input
                    type="text"
                    variant="secondary"
                    value={assetName}
                    onChange={(value) => setAssetName(value)}
                    placeholder="Type a name for the asset"
                    label="Asset Name"
                    className={styles.conversionFormInput}
                    error={error}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '15px' }}>
                    <input
                      type="checkbox"
                      checked={saveMetadata}
                      onChange={(e) => setSaveMetadata(e.target.checked)}
                      className={styles.metadataCheckbox}
                      data-test="pintura-inherit-metadata"
                    />
                    <FormattedMessage {...messages.aiEditInheritMetadata} />
                  </label>
                  <span className={styles.accessChoiceHint}>
                    <FormattedMessage {...messages.aiEditInheritMetadataHint} />
                  </span>

                  <Button type="button" mode="primary" size="small" onClick={handleAddAsset} loading={loading}>
                    <FormattedMessage {...messages.saveNewAsset} />
                  </Button>
                </div>
              </>
            )}
            {showReplaceOptions && (
              <>
                <Button
                  icon={<ArrowLeftCircle />}
                  mode="flat"
                  size="small"
                  onClick={() => setShowReplaceOptions(false)}
                >
                  <FormattedMessage {...messages.goBack} />
                </Button>
                <div className={styles.conversionForm}>
                  <div style={{ color: 'blue' }}>
                    <FormattedMessage {...messages.replaceOriginalImageNote} />
                  </div>

                  <Button
                    type="button"
                    mode="primary"
                    size="small"
                    onClick={handleReplaceOriginalImage}
                    loading={loading}
                  >
                    <FormattedMessage {...messages.replaceOriginalImage} values={{ assetType: 'Image' }} />
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h3>
              <FormattedMessage {...messages.assetUpdateDialogTitle} />
            </h3>
            <div className={styles.AssetDialogButtons}>
              <Button type="button" mode="primary" size="small" onClick={() => setShowNewAssetOptions(true)}>
                <FormattedMessage {...messages.addAsNewAsset} />
              </Button>
              {/* <Button
                type="button"
                mode="decline"
                size="small"
                onClick={() => {
                  handleReplaceOriginal();
                }}
              >
                <FormattedMessage
                  {...messages.replaceOriginalImage}
                  values={{ assetType: assetType === 'original' ? 'Image' : 'Conversion' }}
                />
              </Button> */}

              <Button type="button" mode="primary" size="small" onClick={() => setShowConversionOptions(true)}>
                <FormattedMessage {...messages.addAsAConversion} />
              </Button>

              <Button type="button" mode="tertiary" size="small" onClick={handleDownloadConversion} loading={loading}>
                <FormattedMessage {...messages.downloadEditedCopy} />
              </Button>

              <Button icon={<ArrowLeftCircle />} mode="flat" size="small" onClick={onCancel}>
                <FormattedMessage {...messages.cancel} />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
